import React, { useMemo, useState } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRoute } from "@react-navigation/native";
import { useLazyQuery } from "@apollo/client";
import { addDays, format, isSameDay } from "date-fns";

import Container from "../../components/Container";
import Header from "../../components/Header";
import Text from "../../components/Text";
import Button from "../../components/Button";
import StickyFooter from "../../components/StickyFooter";
import { colors, fonts } from "../../theme";
import { AVAILABLE_APPOINTMENT_SLOTS } from "./gql";

const DURATIONS = [15, 30, 45, 60, 90, 120];
const DAYS_TO_SHOW = 14;

const styles = StyleSheet.create({
  container: { backgroundColor: colors.black, flex: 1 },
  scrollView: { paddingHorizontal: 20 },
  sectionLabel: {
    fontFamily: "RobotoMono_400Regular",
    color: colors.midGrey,
    textTransform: "uppercase",
    fontSize: 11,
    letterSpacing: 1,
    marginTop: 22,
    marginBottom: 10,
  },
  durationRow: { flexDirection: "row", flexWrap: "wrap" },
  durationChip: {
    borderWidth: 1.5,
    borderColor: "#2a2d3f",
    backgroundColor: "#262b42",
    borderRadius: 20,
    paddingVertical: 8,
    paddingHorizontal: 16,
    marginRight: 8,
    marginBottom: 8,
  },
  durationChip__selected: {
    borderColor: colors.yellow,
    backgroundColor: colors.yellow,
  },
  durationText: { color: colors.white, fontSize: 13 },
  durationText__selected: { color: colors.black, fontFamily: fonts.semiBold },
  dateStrip: { flexDirection: "row" },
  dateChip: {
    width: 52,
    alignItems: "center",
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: "#2a2d3f",
    backgroundColor: "#262b42",
    marginRight: 8,
  },
  dateChip__selected: {
    borderColor: colors.yellow,
    backgroundColor: colors.yellow,
  },
  dow: {
    fontFamily: "RobotoMono_400Regular",
    fontSize: 10,
    color: colors.midGrey,
    textTransform: "uppercase",
  },
  dow__selected: { color: "rgba(21,24,41,0.6)" },
  dom: { fontFamily: fonts.bold, fontSize: 15, color: colors.white, marginTop: 2 },
  dom__selected: { color: colors.black },
  slotGrid: { flexDirection: "row", flexWrap: "wrap", marginTop: 10 },
  slot: {
    width: "31%",
    marginRight: "3.5%",
    marginBottom: 10,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: "#2a2d3f",
    backgroundColor: "#262b42",
    alignItems: "center",
  },
  slot__selected: {
    borderColor: colors.green,
    backgroundColor: colors.green,
  },
  slotText: {
    color: colors.white,
    fontFamily: "RobotoMono_400Regular",
    fontSize: 12,
  },
  slotText__selected: { color: "#0d211d", fontFamily: fonts.bold },
  emptyState: { color: colors.midGrey, marginTop: 10 },
});

type Props = { navigation: any };

const DateTimeSelectionScreen: React.FC<Props> = ({ navigation }) => {
  const { params } = useRoute<any>();
  const { store, service, employeeId } = params || {};

  const [duration, setDuration] = useState(60);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState<any>(null);

  const dates = useMemo(
    () => Array.from({ length: DAYS_TO_SHOW }, (_, i) => addDays(new Date(), i)),
    [],
  );

  const [fetchSlots, { data, loading }] = useLazyQuery(
    AVAILABLE_APPOINTMENT_SLOTS,
    { fetchPolicy: "network-only" },
  );

  const runQuery = (date: Date, durationMinutes: number) => {
    setSelectedSlot(null);
    fetchSlots({
      variables: {
        storeId: store.id,
        serviceId: service.id,
        employeeId,
        date: format(date, "yyyy-MM-dd"),
        durationMinutes,
      },
    });
  };

  React.useEffect(() => {
    runQuery(selectedDate, duration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectDate = (date: Date) => {
    setSelectedDate(date);
    runQuery(date, duration);
  };

  const handleSelectDuration = (value: number) => {
    setDuration(value);
    runQuery(selectedDate, value);
  };

  const slots = data?.availableAppointmentSlots || [];

  const handleNext = () => {
    navigation.navigate("ContactInfo", {
      store,
      service,
      employeeId: employeeId || selectedSlot?.availableEmployeeIds?.[0],
      startsAt: selectedSlot?.startsAt,
      durationMinutes: duration,
    });
  };

  return (
    <SafeAreaView style={styles.container} edges={["right", "bottom", "left"]}>
      <Container>
        <ScrollView style={styles.scrollView}>
          <Header
            title="Pick a day and time"
            description="Only real open slots are shown -- nothing double-booked."
          />

          <Text style={styles.sectionLabel}>Duration</Text>
          <View style={styles.durationRow}>
            {DURATIONS.map((value) => {
              const selected = duration === value;
              return (
                <TouchableOpacity
                  key={value}
                  style={[styles.durationChip, selected && styles.durationChip__selected]}
                  onPress={() => handleSelectDuration(value)}
                >
                  <Text style={[styles.durationText, selected && styles.durationText__selected]}>
                    {value < 60 ? `${value} min` : `${value / 60}h${value % 60 ? ` ${value % 60}m` : ""}`}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          <Text style={styles.sectionLabel}>Date</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={styles.dateStrip}>
              {dates.map((date) => {
                const selected = isSameDay(date, selectedDate);
                return (
                  <TouchableOpacity
                    key={date.toISOString()}
                    style={[styles.dateChip, selected && styles.dateChip__selected]}
                    onPress={() => handleSelectDate(date)}
                  >
                    <Text style={[styles.dow, selected && styles.dow__selected]}>
                      {format(date, "EEE")}
                    </Text>
                    <Text style={[styles.dom, selected && styles.dom__selected]}>
                      {format(date, "d")}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </ScrollView>

          <Text style={styles.sectionLabel}>Available times</Text>
          {loading && <ActivityIndicator color={colors.yellow} />}
          {!loading && slots.length === 0 && (
            <Text style={styles.emptyState}>
              No open times on this day -- try another date.
            </Text>
          )}
          <View style={styles.slotGrid}>
            {slots.map((slot: any) => {
              const selected = selectedSlot?.startsAt === slot.startsAt;
              return (
                <TouchableOpacity
                  key={slot.startsAt}
                  style={[styles.slot, selected && styles.slot__selected]}
                  onPress={() => setSelectedSlot(slot)}
                >
                  <Text style={[styles.slotText, selected && styles.slotText__selected]}>
                    {format(new Date(slot.startsAt), "h:mm a")}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </ScrollView>
      </Container>
      <StickyFooter>
        <Button
          title={
            selectedSlot
              ? `Continue -- ${format(new Date(selectedSlot.startsAt), "h:mm a")}`
              : "Continue"
          }
          onPress={handleNext}
          disabled={!selectedSlot}
        />
      </StickyFooter>
    </SafeAreaView>
  );
};

export default DateTimeSelectionScreen;
